import { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const QUESTIONS = [
  {
    id: 'first_impression',
    text: 'When you first opened Eunoia\nhow did it feel?',
    type: 'single',
    options: [
      'warm and safe',
      'curious but uncertain',
      'something I needed',
      'a little strange',
      "I'm not sure yet",
    ],
  },
  {
    id: 'genuine_presence',
    text: 'Did Eunoia feel like a\ngenuine presence or more\nlike a chatbot?',
    type: 'single',
    options: [
      'genuinely present',
      'mostly present',
      'somewhere in between',
      'more like a chatbot',
    ],
  },
  {
    id: 'voice_experience',
    text: 'How did the voice feel?',
    type: 'single',
    options: [
      'warm and human',
      'good but not quite human',
      'slightly off',
      'I prefer text only',
      'I did not use the voice',
    ],
  },
  {
    id: 'emotional_safety',
    text: 'Did you feel safe sharing\nsomething real with Eunoia?',
    type: 'single',
    options: [
      'yes completely',
      'mostly yes',
      'somewhat',
      'not quite',
    ],
  },
  {
    id: 'memory_continuity',
    text: 'Did Eunoia seem to remember\nyou across conversations?',
    type: 'single',
    options: [
      'yes noticeably',
      'slightly',
      'not really',
      'I only had one conversation',
    ],
  },
  {
    id: 'specific_moment',
    text: 'Was there a specific moment\nwhere Eunoia surprised you\nin a good way?',
    type: 'single',
    options: [
      'yes \u2014 something she said',
      'yes \u2014 how she listened',
      'yes \u2014 the voice quality',
      'not yet',
      'still exploring',
    ],
  },
  {
    id: 'biggest_gap',
    text: 'What felt most missing\nor incomplete?',
    type: 'single',
    options: [
      'the voice needs more warmth',
      'responses felt too long',
      'responses felt too short',
      'I wanted her to remember more',
      'the interface needs work',
      'nothing significant',
    ],
  },
  {
    id: 'technical_issues',
    text: 'Did you experience any\ntechnical problems?',
    type: 'single',
    options: [
      'no everything worked',
      'minor glitches',
      'voice did not work',
      'slow responses',
      'login or access issues',
      'other issues',
    ],
  },
  {
    id: 'technical_feedback',
    text: 'Describe any bugs or\ntechnical issues you noticed',
    type: 'text',
    placeholder: 'say what\u2019s true\u2026',
    maxLength: 500,
    optional: true,
  },
  {
    id: 'overall_value',
    text: 'Would you want to keep\nusing Eunoia Presence?',
    type: 'single',
    options: [
      'yes absolutely',
      'yes with improvements',
      'maybe \u2014 need more time',
      'not sure yet',
    ],
  },
  {
    id: 'sharing',
    text: 'Would you recommend Eunoia\nto someone going through\na difficult time?',
    type: 'single',
    options: [
      'yes immediately',
      'yes once it improves',
      'maybe',
      'not yet',
    ],
  },
  {
    id: 'pricing_honesty',
    text: 'If Eunoia costs $14.99/month\nafter the beta how would\nyou feel about that?',
    type: 'single',
    options: [
      'fair for what it offers',
      'I would pay more',
      'slightly expensive',
      'too expensive currently',
      'depends on improvements',
    ],
  },
  {
    id: 'b2b_potential',
    text: 'Can you imagine Eunoia\nbeing used in a professional\nsetting such as employee\nwellness or healthcare support?',
    type: 'single',
    options: [
      'yes absolutely',
      'possibly with modifications',
      'not sure',
      'probably not',
    ],
  },
  {
    id: 'open_reflections',
    text: 'Is there anything else\nyou want us to know about\nyour experience?',
    type: 'text',
    placeholder: 'say what\u2019s true\u2026',
    maxLength: 1000,
    optional: true,
  },
];

const BetaSurvey = ({ onComplete, onRemindLater }) => {
  const [phase, setPhase] = useState('invite');
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [textValue, setTextValue] = useState('');
  const [fadingIn, setFadingIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fadeTransition = useCallback((callback) => {
    setFadingIn(false);
    setTimeout(() => {
      callback();
      setFadingIn(true);
    }, 300);
  }, []);

  const selectOption = (option) => {
    const q = QUESTIONS[currentQ];
    const updated = { ...answers, [q.id]: option };
    setAnswers(updated);
    setTimeout(() => advance(updated), 400);
  };

  const submitText = () => {
    const q = QUESTIONS[currentQ];
    const updated = { ...answers, [q.id]: textValue || '(skipped)' };
    setAnswers(updated);
    advance(updated);
  };

  const advance = (currentAnswers) => {
    const next = currentQ + 1;
    if (next >= QUESTIONS.length) {
      submitSurvey(currentAnswers);
    } else {
      fadeTransition(() => {
        setCurrentQ(next);
        setTextValue('');
      });
    }
  };

  const submitSurvey = async (finalAnswers) => {
    setSubmitting(true);
    try {
      await fetch(`${API}/beta/survey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ responses: finalAnswers }),
      });
    } catch {
      // Store locally as fallback
    }
    localStorage.setItem('beta_survey_completed', 'true');
    fadeTransition(() => setPhase('complete'));
    setSubmitting(false);
  };

  // Auto-dismiss completion screen
  useEffect(() => {
    if (phase === 'complete') {
      const timer = setTimeout(() => onComplete(), 5000);
      return () => clearTimeout(timer);
    }
  }, [phase, onComplete]);

  // Invitation screen
  if (phase === 'invite') {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '0 32px',
      }} data-testid="survey-invite">
        <div style={{ width: '100%', maxWidth: '420px', textAlign: 'center' }} className="fade-in">
          <div className="breathing-sphere" style={{ margin: '0 auto 40px' }} />

          <h2 style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic', fontSize: '24px', fontWeight: 400,
            color: 'rgba(255, 243, 225, 0.82)', marginBottom: '24px',
          }} data-testid="survey-title">your voice matters</h2>

          <p style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '17px', color: 'rgba(255, 243, 225, 0.60)',
            lineHeight: 1.8, marginBottom: '40px',
          }}>
            You are one of the first five people to spend time with Eunoia.
            Your honest experience shapes what she becomes.
            Would you share two minutes with us?
          </p>

          <button
            onClick={() => fadeTransition(() => setPhase('questions'))}
            className="velvet-btn"
            data-testid="survey-start-btn"
            style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '17px', marginBottom: '16px',
            }}
          >yes, share my thoughts</button>

          <button
            onClick={onRemindLater}
            data-testid="survey-remind-btn"
            style={{
              background: 'none', border: 'none', display: 'block',
              width: '100%', cursor: 'pointer', padding: '12px',
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '15px', color: 'rgba(255, 243, 225, 0.35)',
            }}
          >remind me later</button>
        </div>
      </div>
    );
  }

  // Completion screen
  if (phase === 'complete') {
    return (
      <div className="velvet-void" style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '0 32px',
      }} data-testid="survey-complete">
        <div style={{ textAlign: 'center', maxWidth: '420px' }} className="fade-in">
          <div className="breathing-sphere" style={{ margin: '0 auto 40px' }} />
          <p style={{
            fontFamily: "'Cormorant', Georgia, serif",
            fontStyle: 'italic', fontSize: '20px',
            color: 'rgba(255, 243, 225, 0.82)', lineHeight: 1.8,
          }}>
            thank you for this.
            <br /><br />
            your honesty is how Eunoia becomes what she is meant to be —
            a presence that genuinely helps.
            <br /><br />
            you are part of that.
          </p>
        </div>
      </div>
    );
  }

  // Questions flow
  const q = QUESTIONS[currentQ];
  const progress = ((currentQ + 1) / QUESTIONS.length) * 100;

  return (
    <div className="velvet-void" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '0 32px',
    }} data-testid="survey-questions">
      <div style={{
        width: '100%', maxWidth: '440px',
        opacity: fadingIn ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}>
        {/* Progress dots */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '6px',
          marginBottom: '40px',
        }} data-testid="survey-progress">
          {QUESTIONS.map((_, i) => (
            <div key={i} style={{
              width: '6px', height: '6px', borderRadius: '50%',
              background: i <= currentQ
                ? 'rgba(196, 137, 74, 0.70)'
                : 'rgba(255, 243, 225, 0.12)',
              transition: 'background 0.3s ease',
            }} />
          ))}
        </div>

        {/* Question text */}
        <p style={{
          fontFamily: "'Cormorant', Georgia, serif",
          fontStyle: 'italic', fontSize: '22px', fontWeight: 400,
          color: 'rgba(255, 243, 225, 0.82)',
          textAlign: 'center', lineHeight: 1.6,
          marginBottom: '32px', whiteSpace: 'pre-line',
        }} data-testid="survey-question-text">
          {q.text}
        </p>

        {/* Options or text input */}
        {q.type === 'single' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {q.options.map((opt) => (
              <button
                key={opt}
                onClick={() => selectOption(opt)}
                data-testid={`survey-option-${opt.replace(/\s/g, '-')}`}
                style={{
                  background: answers[q.id] === opt
                    ? 'rgba(196, 137, 74, 0.15)'
                    : 'rgba(255, 243, 225, 0.04)',
                  border: `1px solid ${answers[q.id] === opt
                    ? 'rgba(196, 137, 74, 0.35)'
                    : 'rgba(255, 243, 225, 0.08)'}`,
                  borderRadius: '12px',
                  padding: '14px 20px',
                  fontFamily: "'EB Garamond', Georgia, serif",
                  fontSize: '16px',
                  color: 'rgba(255, 243, 225, 0.75)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s ease',
                }}
              >{opt}</button>
            ))}
          </div>
        ) : (
          <div>
            <textarea
              value={textValue}
              onChange={(e) => setTextValue(e.target.value.slice(0, q.maxLength))}
              placeholder={q.placeholder}
              data-testid="survey-text-input"
              style={{
                width: '100%',
                minHeight: '120px',
                background: 'rgba(255, 243, 225, 0.03)',
                border: '1px solid rgba(255, 243, 225, 0.08)',
                borderRadius: '16px',
                padding: '16px',
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: '16px',
                color: 'rgba(255, 243, 225, 0.75)',
                lineHeight: 1.7,
                resize: 'vertical',
                outline: 'none',
                marginBottom: '8px',
                boxSizing: 'border-box',
              }}
            />
            <p style={{
              textAlign: 'right', fontSize: '12px',
              color: 'rgba(255, 243, 225, 0.25)', marginBottom: '20px',
            }}>{textValue.length}/{q.maxLength}</p>
            <button
              onClick={submitText}
              disabled={submitting}
              className="velvet-btn"
              data-testid="survey-text-submit"
              style={{
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: '16px',
              }}
            >{q.optional ? (textValue ? 'continue' : 'skip') : 'continue'}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BetaSurvey;
